import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { motion } from "framer-motion";
import axios from "axios";
import { Users, Mail, Phone, Calendar, Eye, Search, Filter, ChevronDown } from "lucide-react";
import { Input } from "@/components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/v1`;

export default function DashboardLeads() {
  const { getAuthHeader } = useAuth();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    fetchLeads();
  }, []);

  const fetchLeads = async () => {
    try {
      const res = await axios.get(`${API}/dashboard/leads`, { headers: getAuthHeader() });
      setLeads(res.data?.leads || []);
    } catch (err) {
      console.error("Failed to fetch leads:", err);
    } finally {
      setLoading(false);
    }
  };

  const filtered = leads.filter(l =>
    (l.name || "").toLowerCase().includes(search.toLowerCase()) ||
    (l.email || "").toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#7C3AED] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dashboard-leads">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-white font-['Outfit']">Leads</h1>
          <p className="text-[#94A3B8] mt-1">Track inquiries and messages from potential customers</p>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <span className="px-3 py-1.5 bg-[#7C3AED]/20 text-[#7C3AED] rounded-lg font-medium">
            {leads.length} Total
          </span>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search leads..."
          className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-[#64748B]"
          data-testid="leads-search"
        />
      </div>

      {filtered.length > 0 ? (
        <div className="space-y-3">
          {filtered.map((lead, i) => (
            <motion.div
              key={lead.id || i}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              className="bg-[#0F172A] border border-white/10 rounded-xl p-5 hover:border-[#7C3AED]/30 transition-colors"
            >
              <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <h3 className="text-white font-medium">{lead.name || "Anonymous"}</h3>
                  <div className="flex flex-wrap gap-4 mt-2 text-sm text-[#94A3B8]">
                    {lead.email && (
                      <span className="flex items-center gap-1.5">
                        <Mail className="w-3.5 h-3.5" />{lead.email}
                      </span>
                    )}
                    {lead.phone && (
                      <span className="flex items-center gap-1.5">
                        <Phone className="w-3.5 h-3.5" />{lead.phone}
                      </span>
                    )}
                  </div>
                  {lead.message && (
                    <p className="mt-2 text-sm text-[#94A3B8] line-clamp-2">{lead.message}</p>
                  )}
                </div>
                <div className="text-right text-xs text-[#64748B] flex-shrink-0">
                  <div className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {lead.created_at ? new Date(lead.created_at).toLocaleDateString() : "—"}
                  </div>
                  {lead.source && (
                    <span className="mt-1 inline-block px-2 py-0.5 bg-[#7C3AED]/10 text-[#7C3AED] rounded text-xs">
                      {lead.source}
                    </span>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      ) : (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="bg-[#0F172A] border border-white/10 rounded-xl p-12 text-center"
        >
          <Users className="w-16 h-16 text-[#64748B] mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-white mb-2">No Leads Yet</h2>
          <p className="text-[#94A3B8] max-w-md mx-auto">
            When visitors contact you through your listing, their inquiries will appear here. Make sure your listing is published and has contact information.
          </p>
        </motion.div>
      )}
    </div>
  );
}
